import React, { useState } from 'react';
import Modal from '@/components/common/Modal/Modal';
import Button from '@/components/common/Button/Button';
import { claimGiftCard } from '@/services/giftCard/giftCardService';
import { useToast } from '@/hooks/useToast';
import { Target, TrendingUp, DollarSign, CheckCircle } from 'lucide-react';
import styles from './GiftCardClaimModal.module.css';

/**
 * GiftCardClaimModal Component
 * Modal for claiming gift cards with wager information
 */
const GiftCardClaimModal = ({ isOpen, onClose, card, onClaim }) => {
  const [isClaiming, setIsClaiming] = useState(false);
  const { showSuccess, showError } = useToast();

  if (!card) return null;

  // Calculate wager progress
  const totalWagerRequired = card.requiredWagerUSD || card.totalWager || 0;
  const currentWager = card.currentWager || 0;
  const progressPercentage = totalWagerRequired > 0 
    ? Math.min((currentWager / totalWagerRequired) * 100, 100) 
    : 0;
  const remainingWager = Math.max(totalWagerRequired - currentWager, 0);

  const handleClaim = async () => {
    if (!card.cardId) {
      showError('Gift card ID is missing');
      return;
    }

    setIsClaiming(true);
    
    try {
      const response = await claimGiftCard(card.cardId);
      
      if (response.success) {
        showSuccess('Gift card claimed successfully!');
        if (onClaim) {
          onClaim(card.cardId);
        }
        onClose();
      } else {
        showError(response.message || 'Failed to claim gift card');
      }
    } catch (error) {
      // Extract error message from API response
      let errorMessage = 'Failed to claim gift card. Please try again.';
      
      if (error.response?.data) {
        // Check for error field first (e.g., {"error":"Gift card not activated by user"})
        if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } 
        // Check for message field
        else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
        // Check for error in nested structure
        else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showError(errorMessage);
    } finally {
      setIsClaiming(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Claim Gift Card"
      size="lg"
      bgColor="linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%) !important;"
      className={styles.claimModal}
    >
      <div className={styles.modalContent}>
        {/* Left Section - Claim Button with Confirmation Info */}
        <div className={styles.leftSection}>
          <div className={styles.contentWrapper}>
            <h2 className={styles.mainTitle}>Claim Your Reward!</h2>
            <p className={styles.description}>
              Your gift card is ready to be claimed. Click the button below to claim your reward.
            </p>

            {/* Confirmation Info */}
            <div className={styles.confirmationInfo}>
              <div className={styles.confirmationItem}>
                <CheckCircle size={20} className={styles.confirmationIcon} />
                <div className={styles.confirmationContent}>
                  <span className={styles.confirmationLabel}>Card Amount</span>
                  <span className={styles.confirmationValue}>
                    ${card.amount?.toLocaleString('en-US') || 0}
                  </span>
                </div>
              </div>
              <div className={styles.confirmationItem}>
                <CheckCircle size={20} className={styles.confirmationIcon} />
                <div className={styles.confirmationContent}>
                  <span className={styles.confirmationLabel}>Status</span>
                  <span className={styles.confirmationValue}>Ready to Claim</span>
                </div>
              </div>
            </div>

            {/* Claim Button */}
            <div className={styles.buttonWrapper}>
              <Button
                onClick={handleClaim}
                variant="primary"
                disabled={isClaiming}
                className={styles.claimButton}
              >
                {isClaiming ? 'Claiming...' : 'Claim Gift Card'}
              </Button>
            </div>
          </div>
        </div>

        {/* Right Section - Wager Information */}
        <div className={styles.rightSection}>
          <div className={styles.wagerInfoContainer}>
            <div className={styles.wagerHeader}>
              <Target size={24} className={styles.wagerIcon} />
              <h3 className={styles.wagerTitle}>Wager Information</h3>
            </div>

            {/* Wager Requirement */}
            <div className={styles.wagerItem}>
              <div className={styles.wagerItemHeader}>
                <span className={styles.wagerItemLabel}>Wager Requirement</span>
                <span className={styles.wagerItemValue}>
                  {card.wagerRequirement > 0 
                    ? `${card.wagerRequirement}x ($${(totalWagerRequired).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})` 
                    : 'No wager required'}
                </span>
              </div>
            </div>

            {/* Wager Progress */}
            {totalWagerRequired > 0 && (
              <>
                <div className={styles.wagerItem}>
                  <div className={styles.wagerItemHeader}>
                    <span className={styles.wagerItemLabel}>Progress</span>
                    <span className={styles.wagerItemValue}>
                      {progressPercentage.toFixed(1)}%
                    </span>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className={styles.progressBarContainer}>
                  <div 
                    className={styles.progressBar}
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>

                {/* Current / Total */}
                <div className={styles.wagerAmounts}>
                  <div className={styles.wagerAmountItem}>
                    <DollarSign size={18} className={styles.amountIcon} />
                    <div className={styles.amountContent}>
                      <span className={styles.amountLabel}>Current Wager</span>
                      <span className={styles.amountValue}>
                        ${currentWager.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                  <div className={styles.wagerAmountItem}>
                    <TrendingUp size={18} className={styles.amountIcon} />
                    <div className={styles.amountContent}>
                      <span className={styles.amountLabel}>Required Wager</span>
                      <span className={styles.amountValue}>
                        ${totalWagerRequired.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Completion Status */}
                <div className={styles.completionStatus}>
                  <CheckCircle size={20} className={styles.completionIcon} />
                  <span className={styles.completionText}>
                    Wager requirement completed! You can now claim your reward.
                  </span>
                </div>
              </>
            )}

            {totalWagerRequired === 0 && (
              <div className={styles.noWagerInfo}>
                <CheckCircle size={24} className={styles.noWagerIcon} />
                <span>No wager requirement for this gift card</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default GiftCardClaimModal;
